import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useParams, useLocation } from 'react-router-dom'
import {
  Flex,
  Stack,
  HStack,
  Box,
  Button,
  Icon,
  Spacer,
  Text,
  Textarea,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Select,
  useDisclosure,
  Circle,
  Skeleton,
  useToast
} from '@chakra-ui/react'
import { ArrowBackIcon, CheckIcon, DeleteIcon } from '@chakra-ui/icons'
import * as Yup from 'yup'
import { useFormik } from 'formik'

import { QualityIcon } from '../../components/Shared/Icons'
import Score from './Score'
import ConfirmModal from '../Shared/ConfirmModal'
import { useMutateControl, useControl } from '../../hooks/qualityControl'
import { useTechnicians } from '../../hooks/users'
import UploadImage from '../Shared/UploadImage'
import RenderImageContainer from '../Shared/RenderImageContainer'
import useUploadImage from '../../hooks/useUploadImage'
import { TextAreaInput } from '../Shared/inputs'

const validationSchema = Yup.object().shape({
  admissionTime: Yup.string(),
  departureTime: Yup.string().when('admissionTime', (admissionTime, schema) => {
    if (!admissionTime) return schema

    return schema.test({
      test: (departureTime) => !departureTime || departureTime >= admissionTime,
      message: 'Hora de salida debe ser mayor o igual que hora de entrada'
    })
  }),
  ratControl: Yup.bool(),
  map: Yup.number().min(0, 'Evaluación mínima 0'),
  stations: Yup.number().min(0, 'Evaluación mínima 0'),
  numeration: Yup.number().min(0, 'Evaluación mínima 0'),
  exterior: Yup.number().min(0, 'Evaluación mínima 0'),
  interior: Yup.number().min(0, 'Evaluación mínima 0'),
  seals: Yup.number().min(0, 'Evaluación mínima 0'),
  cleanliness: Yup.number().min(0, 'Evaluación mínima 0'),
  capture: Yup.number().min(0, 'Evaluación mínima 0'),
  adhesive: Yup.number().min(0, 'Evaluación mínima 0'),
  bait: Yup.number().min(0, 'Evaluación mínima 0'),
  insectControl: Yup.bool(),
  insecticide: Yup.number().min(0, 'Evaluación mínima 0'),
  uvCleanliness: Yup.number().min(0, 'Evaluación mínima 0'),
  film: Yup.number().min(0, 'Evaluación mínima 0'),
  ins: Yup.number().min(0, 'Evaluación mínima 0'),
  reEntry: Yup.number().min(0, 'Evaluación mínima 0'),
  insecticideAmount: Yup.number().min(0, 'Evaluación mínima 0'),
  stain: Yup.number().min(0, 'Evaluación mínima 0'),
  sanitizationControl: Yup.bool(),
  sanitizerSeal: Yup.number().min(0, 'Evaluación mínima 0'),
  date: Yup.number().min(0, 'Evaluación mínima 0'),
  sealStatus: Yup.number().min(0, 'Evaluación mínima 0'),
  bathroom: Yup.number().min(0, 'Evaluación mínima 0'),
  technicianControl: Yup.bool(),
  epp: Yup.number(),
  tools: Yup.number(),
  pumps: Yup.number(),
  proof: Yup.number(),
  rodentsActivity: Yup.number(),
  auditable: Yup.number(),
  presentation: Yup.number(),
  vehicleControl: Yup.bool(),
  vehicleCleanliness: Yup.number(),
  checklist: Yup.number(),
  waste: Yup.number(),
  spill: Yup.number(),
  materials: Yup.number(),
  clientControl: Yup.bool(),
  communication: Yup.number().min(0, 'Evaluación mínima 0'),
  timeliness: Yup.number().min(0, 'Evaluación mínima 0'),
  cordiality: Yup.number().min(0, 'Evaluación mínima 0'),
  service: Yup.number().min(0, 'Evaluación mínima 0'),
  observations: Yup.string(),
  rucTechnician: Yup.string(),
  technicianName: Yup.string().max(
    30,
    'El nombre debe tener como máximo 30 caracteres'
  ),
  rucSupervisor: Yup.string(),
  generalObservations: Yup.string()
})

const initialValuesImages = {
  rat_control: [],
  insect_control: [],
  sanitization_control: [],
  technician_control: [],
  vehicle_control: []
}

const SCORE_SECTIONS = {
  rat: [
    { field: 'map', title: 'Presentar mapa' },
    { field: 'stations', title: 'N° estaciones coincidente con el mapa' },
    { field: 'numeration', title: 'Presentan numeración las estaciones' },
    { field: 'exterior', title: 'Anclaje adecuado de estaciones exteriores' },
    { field: 'interior', title: 'Anclaje adecuado de estaciones interiores' },
    { field: 'seals', title: 'Sellos actualizados' },
    { field: 'cleanliness', title: 'Limpieza de la estación' },
    { field: 'capture', title: 'Trampas de captura en buen estado' },
    { field: 'adhesive', title: 'Trampa adhesiva en buen estado' },
    { field: 'bait', title: 'Cebos rodenticidas en buen estado' }
  ],
  insect: [
    { field: 'insecticide', title: 'Adecuado uso de insecticida' },
    {
      field: 'uvCleanliness',
      title: 'Presenta estaciones de monitoreo Equipos UV limpio'
    },
    { field: 'film', title: 'Láminas de Equipo UV en buen estado' },
    { field: 'ins', title: 'Adecuada disposición trampas de monitoreo ins' },
    { field: 'reEntry', title: 'Informa tiempo de reingreso' },
    { field: 'insecticideAmount', title: 'Informa cantidad de insecticida' },
    { field: 'stain', title: 'Manchas en superficie aplicada' }
  ],
  sanitization: [
    { field: 'sanitizerSeal', title: 'Presenta sellos sanitizantes' },
    { field: 'date', title: 'Sellos con fecha actualizada' },
    { field: 'sealStatus', title: 'Sellos en buen estado' },
    { field: 'bathroom', title: 'Sellos en todos los baños' }
  ],
  technician: [
    { field: 'epp', title: 'Correcto uso de EPP' },
    { field: 'tools', title: 'Presenta Herramientas necesarias' },
    { field: 'pumps', title: 'Equipo de trabajo en buen estado (bombas)' },
    { field: 'proof', title: 'Presenta comprobante' },
    { field: 'rodentsActivity', title: 'Presenta planilla de actividad roedores' },
    { field: 'auditable', title: 'Presenta planilla auditable' },
    { field: 'presentation', title: 'Presentación personal' }
  ],
  vehicle: [
    { field: 'vehicleCleanliness', title: 'Limpieza del vehículo' },
    { field: 'checklist', title: 'Checklist al día' },
    { field: 'waste', title: 'Contenedor residuos líquidos/sólidos' },
    { field: 'spill', title: 'Kit de derrame' },
    { field: 'materials', title: 'Materiales necesarios para el servicio' }
  ],
  client: [
    { field: 'communication', title: 'El técnico comunica su trabajo' },
    { field: 'timeliness', title: 'El técnico llega puntual al servicio' },
    { field: 'cordiality', title: 'Cordialidad del técnico' },
    { field: 'service', title: 'Evaluación del servicio' }
  ]
}

const SECTION_AVERAGE_FIELDS = {
  rat: [
    'map',
    'bait',
    'adhesive',
    'capture',
    'cleanliness',
    'seals',
    'interior',
    'exterior',
    'numeration',
    'stations'
  ],
  insect: [
    'stain',
    'insecticideAmount',
    'reEntry',
    'ins',
    'film',
    'uvCleanliness',
    'insecticide'
  ],
  sanitization: ['bathroom', 'sealStatus', 'date', 'sanitizerSeal'],
  technician: [
    'presentation',
    'auditable',
    'rodentsActivity',
    'proof',
    'pumps',
    'tools',
    'epp'
  ],
  vehicle: ['materials', 'spill', 'waste', 'checklist', 'vehicleCleanliness'],
  client: ['service', 'cordiality', 'timeliness', 'communication']
}

const SECTION_ENABLED_FLAGS = {
  rat: 'ratControl',
  insect: 'insectControl',
  sanitization: 'sanitizationControl',
  technician: 'technicianControl',
  vehicle: 'vehicleControl',
  client: 'clientControl'
}

const SUPERVISION_SECTIONS = [
  'vehicle',
  'technician',
  'sanitization',
  'rat',
  'insect'
]

const isFilledScore = (value) => {
  const score = Number(value)
  return Number.isFinite(score) && score > 0
}

// Formulario control de calidad
const QualityControl = () => {
  const { images, imgUpload, setImagenObj, setImagenSimple } =
    useUploadImage(initialValuesImages)
  const toast = useToast()
  const { controlId } = useParams()
  const history = useHistory()
  const { data: control } = useControl(controlId)
  const { data: technicians } = useTechnicians()
  const [confirm, setConfirm] = useState(false)
  const [removedScores, setRemovedScores] = useState({})
  const [restoreSelection, setRestoreSelection] = useState({})
  const [scoreTitles, setScoreTitles] = useState(() =>
    Object.values(SCORE_SECTIONS)
      .flat()
      .reduce((acc, item) => ({ ...acc, [item.field]: item.title }), {})
  )
  const scoreConfigStorageKey = `quality_control_score_config_${controlId}`
  const { client, branch } = useSelector((state) => state.visits)
  const { mutate, isLoading: loadingUpdate, reset } = useMutateControl('UPDATE')
  const {
    isOpen: openConfirm,
    onOpen: onOpenConfirm,
    onClose: closeConfirm
  } = useDisclosure()

  const formik = useFormik({
    validationSchema,
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      id: control?.id,
      admissionTime: control?.admission_time,
      departureTime: control?.departure_time,
      admissionPeriod: control?.admission_period,
      departurePeriod: control?.departure_period,
      ratControl: control?.rat_control,
      map: control?.map,
      stations: control?.stations,
      numeration: control?.numeration,
      exterior: control?.exterior,
      interior: control?.interior,
      seals: control?.seals,
      cleanliness: control?.cleanliness,
      capture: control?.capture,
      adhesive: control?.adhesive,
      bait: control?.bait,
      insectControl: control?.insect_control,
      insecticide: control?.insecticide,
      uvCleanliness: control?.uv_cleanliness,
      film: control?.film,
      ins: control?.ins,
      reEntry: control?.re_entry,
      insecticideAmount: control?.insecticide_amount,
      stain: control?.stain,
      sanitizationControl: control?.sanitization_control,
      sanitizerSeal: control?.sanitizer_seal,
      date: control?.date,
      sealStatus: control?.seal_status,
      bathroom: control?.bathroom,
      technicianControl: control?.technician_control,
      epp: control?.epp,
      tools: control?.tools,
      pumps: control?.pumps,
      proof: control?.proof,
      rodentsActivity: control?.rodents_activity,
      auditable: control?.auditable,
      presentation: control?.presentation,
      vehicleControl: control?.vehicle_control,
      vehicleCleanliness: control?.vehicle_cleanliness,
      checklist: control?.checklist,
      waste: control?.waste,
      spill: control?.spill,
      materials: control?.materials,
      clientControl: control?.client_control,
      communication: control?.communication,
      timeliness: control?.timeliness,
      cordiality: control?.cordiality,
      service: control?.service,
      observations: control?.observations,
      rucTechnician: control?.ruc_technician,
      technicianName: control?.technician_name,
      rucSupervisor: control?.ruc_supervisor,
      generalObservations: control?.general_observations,
      technicianSignature: control?.technician_signature,
      supervisorSignature: control?.supervisor_signature,
      aditionalNames: control?.aditional_names || []
    },
    onSubmit: (values) => {
      let formData = {
        id: values.id,
        admission_time: values.admissionTime,
        admission_period: values.admissionPeriod,
        departure_time: values.departureTime,
        departure_period: values.departurePeriod,
        rat_control: values.ratControl,
        map: values.map,
        stations: values.stations,
        numeration: values.numeration,
        exterior: values.exterior,
        interior: values.interior,
        seals: values.seals,
        cleanliness: values.cleanliness,
        capture: values.capture,
        adhesive: values.adhesive,
        bait: values.bait,
        insect_control: values.insectControl,
        insecticide: values.insecticide,
        uv_cleanliness: values.uvCleanliness,
        film: values.film,
        ins: values.ins,
        re_entry: values.reEntry,
        insecticide_amount: values.insecticideAmount,
        stain: values.stain,
        sanitization_control: values.sanitizationControl,
        sanitizer_seal: values.sanitizerSeal,
        date: values.date,
        seal_status: values.sealStatus,
        bathroom: values.bathroom,
        technician_control: values.technicianControl,
        epp: values.epp,
        tools: values.tools,
        pumps: values.pumps,
        proof: values.proof,
        rodents_activity: values.rodentsActivity,
        auditable: values.auditable,
        presentation: values.presentation,
        vehicle_control: values.vehicleControl,
        vehicle_cleanliness: values.vehicleCleanliness,
        checklist: values.checklist,
        waste: values.waste,
        spill: values.spill,
        materials: values.materials,
        client_control: values.clientControl,
        communication: values.communication,
        timeliness: values.timeliness,
        cordiality: values.cordiality,
        service: values.service,
        technician_signature: values.technicianSignature,
        ruc_technician: values.rucTechnician,
        technician_name: values.technicianName,
        supervisor_signature: values.supervisorSignature,
        ruc_supervisor: values.rucSupervisor,
        observations: values.observations,
        general_observations: values.generalObservations,
        aditional_names: values.aditionalNames,
        score_config: {
          removedScores,
          scoreTitles
        }
      }

      Object.keys(images).forEach((key) => {
        if (images[key].length > 0) {
          formData[`${key}_images`] = images[key]
        }
      })

      mutate(formData, {
        onSuccess: () => {
          reset()
          formik.resetForm()
          setConfirm(true)
        },
        onError: (err) => {
          reset()
          if (err.request.response.includes('ERROR_SAVING_QUALITY_CONTROL')) {
            toast({
              title: `Error al guardar datos`,
              status: 'error',
              isClosable: true
            })
          } else if (err.request.response.includes('ERROR_GENERATING_PDF')) {
            toast({
              title: `Error al crear PDF, datos guardados sin problemas`,
              status: 'error',
              isClosable: true
            })
          } else {
            toast({
              title: `Error indefinido`,
              status: 'error',
              isClosable: true
            })
          }
        }
      })
    }
  })

  const location = useLocation();
  const handleBack = () => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    const tabQuery = tabParam ? `?tab=${tabParam}` : '';
    history.push(`/visitas/${control.id_visit}${tabQuery}`);
  }

  // Calcular promedio solo con campos llenados (nota > 0), igual que el backend/PDF
  const avg = (values) => {
    const filled = values.filter(isFilledScore)
    if (!filled.length) return 0
    const sum = filled.reduce((acc, current) => acc + Number(current), 0)
    return sum / filled.length
  }

  const getVisibleFields = (sectionKey) =>
    SECTION_AVERAGE_FIELDS[sectionKey].filter((field) => !removedScores[field])

  const getSectionAverage = (sectionKey) => {
    const enabledFlag = SECTION_ENABLED_FLAGS[sectionKey]
    if (enabledFlag && !formik.values[enabledFlag]) {
      return '0.00'
    }
    const fields = getVisibleFields(sectionKey)
    return avg(fields.map((field) => formik.values[field])).toFixed(2)
  }

  const getSupervisionAverage = () => {
    const fields = SUPERVISION_SECTIONS.filter(
      (sectionKey) => formik.values[SECTION_ENABLED_FLAGS[sectionKey]]
    ).flatMap((sectionKey) => getVisibleFields(sectionKey))
    return avg(fields.map((field) => formik.values[field])).toFixed(2)
  }

  const handleDeleteScorePoint = (fieldName) => {
    setRemovedScores((prev) => ({ ...prev, [fieldName]: true }))
    formik.setFieldValue(fieldName, 0)
  }

  const handleRestoreScorePoint = (sectionKey) => {
    const fieldName = restoreSelection[sectionKey]
    if (!fieldName) return

    setRemovedScores((prev) => {
      const next = { ...prev }
      delete next[fieldName]
      return next
    })
    setRestoreSelection((prev) => ({ ...prev, [sectionKey]: '' }))
  }

  const handleTitleChange = (fieldName, value) => {
    setScoreTitles((prev) => ({ ...prev, [fieldName]: value }))
  }

  const renderScoreSection = (sectionKey) => {
    const points = SCORE_SECTIONS[sectionKey].filter(
      (point) => !removedScores[point.field]
    )
    const removedPoints = SCORE_SECTIONS[sectionKey].filter(
      (point) => removedScores[point.field]
    )

    return (
      <Stack spacing={2}>
        {points.length ? (
          <HStack spacing="24px" overflowX={'scroll'} py={3} px={2}>
            {points.map((point) => (
              <Score
                key={point.field}
                title={scoreTitles[point.field] || point.title}
                score={formik.values[point.field]}
                setScore={formik.setFieldValue}
                name={point.field}
                editableTitle
                onTitleChange={(newValue) => handleTitleChange(point.field, newValue)}
                onDelete={() => handleDeleteScorePoint(point.field)}
              />
            ))}
          </HStack>
        ) : (
          <Text fontSize="14px" color="#666587" px={2}>
            No hay puntos activos en esta área.
          </Text>
        )}

        {removedPoints.length ? (
          <HStack px={2} pb={2}>
            <Select
              size="sm"
              placeholder="Selecciona un punto eliminado"
              value={restoreSelection[sectionKey] || ''}
              onChange={(e) =>
                setRestoreSelection((prev) => ({
                  ...prev,
                  [sectionKey]: e.target.value
                }))
              }
            >
              {removedPoints.map((point) => (
                <option key={point.field} value={point.field}>
                  {scoreTitles[point.field] || point.title}
                </option>
              ))}
            </Select>
            <Button
              size="sm"
              onClick={() => handleRestoreScorePoint(sectionKey)}
              disabled={!restoreSelection[sectionKey]}
            >
              Mostrar punto
            </Button>
          </HStack>
        ) : null}
      </Stack>
    )
  }

  const handleClickConfirm = () => {
    onOpenConfirm()
  }

  // Guardar firmas en el estado
  useEffect(() => {
    if (control) {
      setImagenSimple({
        rat_control:
          control?.rat_control_images || initialValuesImages.rat_control,
        insect_control:
          control?.insect_control_images || initialValuesImages.insect_control,
        sanitization_control:
          control?.sanitization_control_images ||
          initialValuesImages.sanitization_control,
        technician_control:
          control?.technician_control_images ||
          initialValuesImages.technician_control,
        vehicle_control:
          control?.vehicle_control_images || initialValuesImages.vehicle_control
      })
    }
  }, [control])

  useEffect(() => {
    const rawConfig = localStorage.getItem(scoreConfigStorageKey)
    if (!rawConfig) return

    try {
      const parsed = JSON.parse(rawConfig)
      if (parsed?.removedScores) setRemovedScores(parsed.removedScores)
      if (parsed?.scoreTitles) {
        setScoreTitles((prev) => ({ ...prev, ...parsed.scoreTitles }))
      }
    } catch (error) {
      console.error('Error reading quality score config from storage', error)
    }
  }, [scoreConfigStorageKey])

  useEffect(() => {
    localStorage.setItem(
      scoreConfigStorageKey,
      JSON.stringify({ removedScores, scoreTitles })
    )
  }, [scoreConfigStorageKey, removedScores, scoreTitles])

  const changeTime = (fieldName, value) => {
    const dict = {
      departureTime: 'departurePeriod',
      admissionTime: 'admissionPeriod'
    }

    const partsHours = value.split(':')
    const hourNum = parseInt(partsHours[0], 10)

    const isAm = Boolean(hourNum >= 0 && hourNum < 12)

    formik.setFieldValue(fieldName, value)
    formik.setFieldValue(dict[fieldName], isAm ? 'AM' : 'PM')
  }

  const addNewNameField = () => {
    formik.setFieldValue('aditionalNames', [
      ...formik.values.aditionalNames,
      { name: '' }
    ])
  }

  const removeAditionalName = (index) => {
    const newNames = formik.values.aditionalNames.filter((_, i) => i !== index)
    formik.setFieldValue('aditionalNames', newNames)
  }

  const techniciansOptions = technicians?.items || []

  return (
    <Stack spacing={8} py={12} px={6}>
      <Flex spacing={10} align="right" pt="15px">
        <Button
          onClick={handleBack}
          bg="#FFFFFF"
          color="#666587"
          border="1px"
          borderStyle="solid"
          borderRadius="lg"
          borderColor="#F2F1FB"
          leftIcon={<ArrowBackIcon />}
        >
          Documentos de visita
        </Button>
      </Flex>
      <Box w="100%" bg="#F2F1FB80" pb={5}>
        <Flex spacing={10} align="left" pt="15px">
          <Stack spacing={2} pl={30}>
            <Text fontSize="10px" color="#36355F">
              Cliente
            </Text>
            <Text fontSize="17px" fontWeight="bold" color="#36355F">
              {client}
            </Text>
          </Stack>
          <Stack spacing={2} pl={30}>
            <Text fontSize="10px" color="#36355F">
              Sucursal
            </Text>
            <Text fontSize="17px" fontWeight="bold" color="#36355F">
              {branch}
            </Text>
          </Stack>
        </Flex>
      </Box>
      {control ? (
        <>
          <Box w="100%" bg="#F2F1FB" pb={5}>
            <Flex spacing={10} align="left" pt="15px">
              <Stack spacing={2} pl={30}>
                <Text fontSize="15px" color="#36355F">
                  <Icon mr="4" fontSize="22" as={QualityIcon} color="#36355F" />
                  Control de calidad
                </Text>
              </Stack>
              <Spacer />
              <Stack spacing={2} pr={30}>
                <Text fontSize="10px" color="#36355F">
                  Serial {control.id}
                </Text>
              </Stack>
            </Flex>
          </Box>
          <Box w="100%" bg="#D1F1F5" pb={5} borderRadius="20px">
            <Flex spacing={10} align="left" pt="15px">
              <Stack spacing={2} pl={30}>
                <Text fontSize="17px" fontWeight="bold" color="#36355F">
                  Datos generales
                </Text>
              </Stack>
            </Flex>
          </Box>
          <FormControl
            id="admissionTime"
            isInvalid={
              formik.touched.admissionTime &&
              Boolean(formik.errors.admissionTime)
            }
          >
            <FormLabel>Hora de ingreso</FormLabel>
            <Input
              name="admissionTime"
              value={formik.values.admissionTime}
              onChange={(e) => changeTime('admissionTime', e.target.value)}
              type="time"
              onBlur={formik.handleBlur}
            />
            <FormErrorMessage>
              {formik.touched.admissionTime && formik.errors.admissionTime}
            </FormErrorMessage>
          </FormControl>
          <FormControl
            id="departureTime"
            isInvalid={
              formik.touched.departureTime &&
              Boolean(formik.errors.departureTime)
            }
          >
            <FormLabel>Hora de salida</FormLabel>
            <Input
              name="departureTime"
              value={formik.values.departureTime}
              onChange={(e) => changeTime('departureTime', e.target.value)}
              type="time"
              onBlur={formik.handleBlur}
            />
            <FormErrorMessage>
              {formik.touched.departureTime && formik.errors.departureTime}
            </FormErrorMessage>
          </FormControl>
          <Box w="100%" bg="#D1F1F5" pb={5} borderRadius="20px">
            <Flex spacing={10} align="left" pt="15px" pr="5px">
              <Stack spacing={2} pl={30}>
                <Text fontSize="17px" fontWeight="bold" color="#36355F">
                  Evaluación servicio desratización
                </Text>
              </Stack>
              <Spacer />
              <Circle
                size="30px"
                bg={formik.values.ratControl ? 'green' : '#E6E6EF'}
                color="white"
                as="button"
                onClick={() =>
                  formik.setFieldValue('ratControl', !formik.values.ratControl)
                }
              >
                <CheckIcon />
              </Circle>
            </Flex>
          </Box>

          {formik.values.ratControl && (
            <Flex direction="column" gap={2}>
              {renderScoreSection('rat')}
              <Box
                w="100%"
                bg="#FFFFFF"
                pb={5}
                borderRadius="20px"
                border={'solid'}
                borderColor="#D1F1F5"
              >
                <Flex spacing={10} align="left" pt="15px" pr="5px">
                  <Stack spacing={2} pl={30}>
                    <Text fontSize="17px" fontWeight="bold" color="#36355F">
                      Promedio desratización
                    </Text>
                  </Stack>
                  <Spacer />
                  <Stack spacing={2} pr={10}>
                    <Text fontSize="25px" fontWeight="bold" color="#59BABA">
                      {getSectionAverage('rat')}
                    </Text>
                  </Stack>
                </Flex>
              </Box>
              <UploadImage
                imgUpload={(formImage) => imgUpload(formImage, 'rat_control')}
                multiple
              />
              {images?.rat_control?.length > 0 && (
                <RenderImageContainer
                  images={images?.rat_control}
                  getNewArray={(arr) => setImagenObj('rat_control', arr)}
                  getNewArrayDelete={(arr) => setImagenObj('rat_control', arr)}
                />
              )}
            </Flex>
          )}

          <Box w="100%" bg="#D1F1F5" pb={5} borderRadius="20px">
            <Flex spacing={10} align="left" pt="15px" pr="5px">
              <Stack spacing={2} pl={30}>
                <Text fontSize="17px" fontWeight="bold" color="#36355F">
                  Evaluación Servicio de Desinsectación
                </Text>
              </Stack>
              <Spacer />
              <Circle
                size="30px"
                bg={formik.values.insectControl ? 'green' : '#E6E6EF'}
                color="white"
                as="button"
                onClick={() =>
                  formik.setFieldValue(
                    'insectControl',
                    !formik.values.insectControl
                  )
                }
              >
                <CheckIcon />
              </Circle>
            </Flex>
          </Box>
          {formik.values.insectControl && (
            <Flex direction="column" gap={2}>
              {renderScoreSection('insect')}
              <Box
                w="100%"
                bg="#FFFFFF"
                pb={5}
                borderRadius="20px"
                border={'solid'}
                borderColor="#D1F1F5"
              >
                <Flex spacing={10} align="left" pt="15px" pr="5px">
                  <Stack spacing={2} pl={30}>
                    <Text fontSize="17px" fontWeight="bold" color="#36355F">
                      Promedio desinsectación
                    </Text>
                  </Stack>
                  <Spacer />
                  <Stack spacing={2} pr={10}>
                    <Text fontSize="25px" fontWeight="bold" color="#59BABA">
                      {getSectionAverage('insect')}
                    </Text>
                  </Stack>
                </Flex>
              </Box>
              <UploadImage
                imgUpload={(formImage) =>
                  imgUpload(formImage, 'insect_control')
                }
                multiple
              />
              {images?.insect_control?.length > 0 && (
                <RenderImageContainer
                  images={images?.insect_control}
                  getNewArray={(arr) => setImagenObj('insect_control', arr)}
                  getNewArrayDelete={(arr) =>
                    setImagenObj('insect_control', arr)
                  }
                />
              )}
            </Flex>
          )}
          <Box w="100%" bg="#D1F1F5" pb={5} borderRadius="20px">
            <Flex spacing={10} align="left" pt="15px" pr="5px">
              <Stack spacing={2} pl={30}>
                <Text fontSize="17px" fontWeight="bold" color="#36355F">
                  Evaluación servicio de sanitización
                </Text>
              </Stack>
              <Spacer />
              <Circle
                size="30px"
                bg={formik.values.sanitizationControl ? 'green' : '#E6E6EF'}
                color="white"
                as="button"
                onClick={() =>
                  formik.setFieldValue(
                    'sanitizationControl',
                    !formik.values.sanitizationControl
                  )
                }
              >
                <CheckIcon />
              </Circle>
            </Flex>
          </Box>
          {formik.values.sanitizationControl && (
            <Flex direction="column" gap={2}>
              {renderScoreSection('sanitization')}
              <Box
                w="100%"
                bg="#FFFFFF"
                pb={5}
                borderRadius="20px"
                border={'solid'}
                borderColor="#D1F1F5"
              >
                <Flex spacing={10} align="left" pt="15px" pr="5px">
                  <Stack spacing={2} pl={30}>
                    <Text fontSize="17px" fontWeight="bold" color="#36355F">
                      Promedio sanitización
                    </Text>
                  </Stack>
                  <Spacer />
                  <Stack spacing={2} pr={10}>
                    <Text fontSize="25px" fontWeight="bold" color="#59BABA">
                      {getSectionAverage('sanitization')}
                    </Text>
                  </Stack>
                </Flex>
              </Box>
              <UploadImage
                imgUpload={(formImage) =>
                  imgUpload(formImage, 'sanitization_control')
                }
                multiple
              />
              {images?.sanitization_control?.length > 0 && (
                <RenderImageContainer
                  images={images?.sanitization_control}
                  getNewArray={(arr) =>
                    setImagenObj('sanitization_control', arr)
                  }
                  getNewArrayDelete={(arr) =>
                    setImagenObj('sanitization_control', arr)
                  }
                />
              )}
            </Flex>
          )}
          <Box w="100%" bg="#D1F1F5" pb={5} borderRadius="20px">
            <Flex spacing={10} align="left" pt="15px" pr="5px">
              <Stack spacing={2} pl={30}>
                <Text fontSize="17px" fontWeight="bold" color="#36355F">
                  Inspección Técnico
                </Text>
              </Stack>
              <Spacer />
              <Circle
                size="30px"
                bg={formik.values.technicianControl ? 'green' : '#E6E6EF'}
                color="white"
                as="button"
                onClick={() =>
                  formik.setFieldValue(
                    'technicianControl',
                    !formik.values.technicianControl
                  )
                }
              >
                <CheckIcon />
              </Circle>
            </Flex>
          </Box>
          {formik.values.technicianControl && (
            <Flex direction="column" gap={2}>
              {renderScoreSection('technician')}
              <Box
                w="100%"
                bg="#FFFFFF"
                pb={5}
                borderRadius="20px"
                border={'solid'}
                borderColor="#D1F1F5"
              >
                <Flex spacing={10} align="left" pt="15px" pr="5px">
                  <Stack spacing={2} pl={30}>
                    <Text fontSize="17px" fontWeight="bold" color="#36355F">
                      Promedio técnico
                    </Text>
                  </Stack>
                  <Spacer />
                  <Stack spacing={2} pr={10}>
                    <Text fontSize="25px" fontWeight="bold" color="#59BABA">
                      {getSectionAverage('technician')}
                    </Text>
                  </Stack>
                </Flex>
              </Box>
              <UploadImage
                imgUpload={(formImage) =>
                  imgUpload(formImage, 'technician_control')
                }
                multiple
              />
              {images?.technician_control?.length > 0 && (
                <RenderImageContainer
                  images={images?.technician_control}
                  getNewArray={(arr) => setImagenObj('technician_control', arr)}
                  getNewArrayDelete={(arr) =>
                    setImagenObj('technician_control', arr)
                  }
                />
              )}
            </Flex>
          )}
          <Box w="100%" bg="#D1F1F5" pb={5} borderRadius="20px">
            <Flex spacing={10} align="left" pt="15px" pr="5px">
              <Stack spacing={2} pl={30}>
                <Text fontSize="17px" fontWeight="bold" color="#36355F">
                  Inspección vehiculo
                </Text>
              </Stack>
              <Spacer />
              <Circle
                size="30px"
                bg={formik.values.vehicleControl ? 'green' : '#E6E6EF'}
                color="white"
                as="button"
                onClick={() =>
                  formik.setFieldValue(
                    'vehicleControl',
                    !formik.values.vehicleControl
                  )
                }
              >
                <CheckIcon />
              </Circle>
            </Flex>
          </Box>
          {formik.values.vehicleControl && (
            <Flex direction="column" gap={2}>
              {renderScoreSection('vehicle')}
              <Box
                w="100%"
                bg="#FFFFFF"
                pb={5}
                borderRadius="20px"
                border={'solid'}
                borderColor="#D1F1F5"
              >
                <Flex spacing={10} align="left" pt="15px" pr="5px">
                  <Stack spacing={2} pl={30}>
                    <Text fontSize="17px" fontWeight="bold" color="#36355F">
                      Promedio vehiculo
                    </Text>
                  </Stack>
                  <Spacer />
                  <Stack spacing={2} pr={10}>
                    <Text fontSize="25px" fontWeight="bold" color="#59BABA">
                      {getSectionAverage('vehicle')}
                    </Text>
                  </Stack>
                </Flex>
              </Box>
              <UploadImage
                imgUpload={(formImage) =>
                  imgUpload(formImage, 'vehicle_control')
                }
                multiple
              />
              {images?.vehicle_control?.length > 0 && (
                <RenderImageContainer
                  images={images?.vehicle_control}
                  getNewArray={(arr) => setImagenObj('vehicle_control', arr)}
                  getNewArrayDelete={(arr) =>
                    setImagenObj('vehicle_control', arr)
                  }
                />
              )}
            </Flex>
          )}
          <Box w="100%" bg="#D1F1F5" pb={5} borderRadius="20px">
            <Flex spacing={10} align="left" pt="15px" pr="5px">
              <Stack spacing={2} pl={30}>
                <Text fontSize="17px" fontWeight="bold" color="#36355F">
                  Evaluación del cliente
                </Text>
              </Stack>
              <Spacer />
              <Circle
                size="30px"
                bg={formik.values.clientControl ? 'green' : '#E6E6EF'}
                color="white"
                as="button"
                onClick={() =>
                  formik.setFieldValue(
                    'clientControl',
                    !formik.values.clientControl
                  )
                }
              >
                <CheckIcon />
              </Circle>
            </Flex>
          </Box>
          {formik.values.clientControl && (
            <>
              {renderScoreSection('client')}
              <FormControl
                id="observations"
                isInvalid={
                  formik.touched.observations &&
                  Boolean(formik.errors.observations)
                }
              >
                <FormLabel>Observaciones</FormLabel>
                <Textarea
                  name="observations"
                  value={formik.values.observations}
                  onChange={formik.handleChange}
                  placeholder="Observaciones"
                  h="20%"
                />
                <FormErrorMessage>
                  {formik.touched.observations && formik.errors.observations}
                </FormErrorMessage>
              </FormControl>
            </>
          )}
          <Box w="100%" bg="#D1F1F5" pb={5} borderRadius="20px">
            <Flex spacing={10} align="left" pt="15px" pr="5px">
              <Stack spacing={2} pl={30}>
                <Text fontSize="17px" fontWeight="bold" color="#36355F">
                  Promedio de evaluación del cliente
                </Text>
              </Stack>
              <Spacer />
              <Stack spacing={2} pr={10}>
                <Text fontSize="25px" fontWeight="bold" color="#59BABA">
                  {getSectionAverage('client')}
                </Text>
              </Stack>
            </Flex>
          </Box>
          <Box w="100%" bg="#D1F1F5" pb={5} borderRadius="20px">
            <Flex spacing={10} align="left" pt="15px" pr="5px">
              <Stack spacing={2} pl={30}>
                <Text fontSize="17px" fontWeight="bold" color="#36355F">
                  Promedio de evaluación de supervisión
                </Text>
              </Stack>
              <Spacer />
              <Stack spacing={2} pr={10}>
                <Text fontSize="25px" fontWeight="bold" color="#59BABA">
                  {getSupervisionAverage()}
                </Text>
              </Stack>
            </Flex>
          </Box>
          <Text fontSize="17px" fontWeight="bold" color="#36355F">
            Tecnicos evaluados
          </Text>
          {formik.values.aditionalNames.map((name, index) => (
            <Flex key={index} w="100%">
              <FormControl>
                <FormLabel>Nombre {index + 1}</FormLabel>
                <HStack>
                  <Select
                    placeholder="Seleccione técnico"
                    value={formik.values.aditionalNames[index].name || ''}
                    onChange={(e) =>
                      formik.setFieldValue(
                        `aditionalNames.${index}.name`,
                        e.target.value
                      )
                    }
                  >
                    {techniciansOptions.map((technician) => (
                      <option key={technician.id} value={technician.name}>
                        {technician.name}
                      </option>
                    ))}
                  </Select>
                  <Button onClick={() => removeAditionalName(index)}>
                    <DeleteIcon />
                  </Button>
                </HStack>
              </FormControl>
            </Flex>
          ))}
          <Flex w="100%" justify="flex-end">
            <Button
              isFullWidth
              onClick={addNewNameField}
              disabled={formik.values.aditionalNames.length === 4}
            >
              Agregar nuevo nombre
            </Button>
          </Flex>
          {/* <SignatureModal
            label="Firma del técnico"
            formik={formik}
            values={{
              ruc: formik.values?.rucTechnician,
              fullName: formik.values?.technicianName,
              signature: formik.values?.technicianSignature
            }}
            fieldsNames={{
              ruc: 'rucTechnician',
              fullName: 'technicianName',
              signature: 'technicianSignature'
            }}
            fieldRequired={{
              fullName: true
            }}
          />
          <SignatureModal
            label="Firma del supervisor"
            formik={formik}
            values={{
              ruc: formik.values?.rucSupervisor,
              signature: formik.values?.supervisorSignature
            }}
            fieldsNames={{
              ruc: 'rucSupervisor',
              signature: 'supervisorSignature'
            }}
          /> */}
          <Flex w="100%">
            <TextAreaInput
              name="generalObservations"
              label="Observaciones Generales"
              placeholder="Observaciones generales"
              value={formik.values.generalObservations}
              onChange={formik.handleChange}
              errors={
                formik.touched.generalObservations &&
                Boolean(formik.errors.generalObservations)
              }
              errorMsg={formik.errors.generalObservations}
            />
          </Flex>
          <Stack spacing={10} align={'center'}>
            <Button
              align="center"
              isLoading={loadingUpdate}
              onClick={handleClickConfirm}
              bg="#FFFFFF"
              color="#59BABA"
              border="1px"
              borderColor="#59BABA"
              disabled={loadingUpdate}
            >
              Guardar documento
            </Button>
            {openConfirm && (
              <ConfirmModal
                isOpen={openConfirm}
                onClose={closeConfirm}
                route={`/visitas/${control.id_visit}`}
                confirm={confirm}
                setConfirm={setConfirm}
                handleSubmit={formik.handleSubmit}
                loading={loadingUpdate}
                tabIndex={window.location.search.includes('tab=1') ? 1 : 0}
              />
            )}
          </Stack>
        </>
      ) : (
        <Stack>
          <Skeleton height="20px" />
          <Skeleton height="20px" />
          <Skeleton height="20px" />
        </Stack>
      )}
    </Stack>
  )
}

export default QualityControl
